import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { ArrowUpRight, Flame, ChevronRight, Zap } from "lucide-react";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";

const BUCKETS = [
    { key: "all", label: "All Rungs" },
    { key: "ownership_story", label: "Ownership" },
    { key: "buyers_guide", label: "Buyer's Guide" },
    { key: "spec_deep_dive", label: "Spec" },
    { key: "market_intel", label: "Market" },
    { key: "real_owner_story", label: "Real Owner" },
];

const LADDER = [
    { rung: 1, trim: "R/T", hp: 370 },
    { rung: 2, trim: "Scat Pack", hp: 485 },
    { rung: 3, trim: "SRT 392", hp: 485 },
    { rung: 4, trim: "Hellcat", hp: 717 },
    { rung: 5, trim: "Redeye", hp: 797 },
    { rung: 6, trim: "Trackhawk", hp: 707 },
    { rung: 7, trim: "Demon 170", hp: 1025 },
    { rung: 8, trim: "TRX", hp: 702 },
];

export default function Blog() {
    const [bucket, setBucket] = useState("all");
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/blog/posts?bucket=${bucket}`);
                setPosts(Array.isArray(data) ? data : []);
            } catch {
                setPosts([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [bucket]);

    return (
        <div className="min-h-screen bg-onyx-950 text-white brain-noise">
            <Helmet>
                <title>The Ladder · SRT Finder</title>
                <meta
                    name="description"
                    content="Climb the factory power ladder — R/T through Demon 170. Buyer's guides, spec deep dives, market intel, real owner stories."
                />
            </Helmet>

            {/* Header */}
            <header
                className="border-b border-onyx-700 sticky top-0 z-40 backdrop-blur-md"
                style={{ background: "rgba(5,5,5,0.85)" }}
            >
                <div className="max-w-7xl mx-auto px-5 py-3 flex items-center justify-between">
                    <Logo size="sm" />
                    <Link
                        to="/marketplace"
                        className="label-caps text-onyx-500 hover:text-hellcat flex items-center gap-1 transition-colors"
                        data-testid="the-ladder-nav-marketplace"
                    >
                        Marketplace <ChevronRight className="h-4 w-4" />
                    </Link>
                </div>
            </header>

            <LadderHero />

            {/* Bucket filter */}
            <div
                className="sticky top-[52px] z-30 backdrop-blur-md border-b border-onyx-700"
                style={{ background: "rgba(5,5,5,0.92)" }}
            >
                <div className="max-w-7xl mx-auto px-5 py-3 flex gap-2 overflow-x-auto no-scrollbar">
                    {BUCKETS.map((b) => (
                        <button
                            key={b.key}
                            onClick={() => setBucket(b.key)}
                            data-testid={`bucket-filter-${b.key}`}
                            className={`whitespace-nowrap px-4 py-1.5 label-caps border transition-all duration-150 ${
                                bucket === b.key
                                    ? "bg-hellcat border-hellcat text-white glow-red"
                                    : "border-onyx-600 text-onyx-500 hover:text-white hover:border-hellcat"
                            }`}
                            style={{ borderRadius: 2 }}
                        >
                            {b.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Posts */}
            <main className="max-w-7xl mx-auto px-5 py-14">
                {loading ? (
                    <div className="grid md:grid-cols-2 gap-5">
                        {[0, 1, 2, 3].map((i) => (
                            <div key={i} className="h-64 brain-panel animate-pulse" />
                        ))}
                    </div>
                ) : posts.length === 0 ? (
                    <EmptyState bucket={bucket} />
                ) : (
                    <div className="grid md:grid-cols-2 gap-5">
                        {posts.map((p, idx) => (
                            <PostCard key={p.slug || idx} post={p} idx={idx} />
                        ))}
                    </div>
                )}
            </main>

            <footer className="border-t border-onyx-700 py-10 text-center label-caps text-onyx-600">
                <p>The Ladder · Authored by Remi · For the Crew, Not the Crawlers</p>
                <p className="mt-2 normal-case tracking-normal text-xs">
                    Ownership stories are illustrative composites. Real owner stories are labeled.
                </p>
            </footer>
        </div>
    );
}

/* ================= LADDER HERO ================= */
function LadderHero() {
    const heroRef = useRef(null);
    const railRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: railRef, offset: ["start end", "end start"] });
    const railFill = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

    return (
        <section
            ref={heroRef}
            className="relative overflow-hidden border-b border-onyx-700"
            style={{ background: "radial-gradient(ellipse at 30% 20%, rgba(225,6,0,0.18) 0%, transparent 55%), #050505" }}
        >
            <div className="brain-scanlines absolute inset-0 pointer-events-none opacity-60" />

            <div className="relative max-w-7xl mx-auto px-5 pt-16 pb-10 md:pt-24 md:pb-16">
                <div className="flex items-center gap-2 label-caps text-hellcat mb-4">
                    <Flame className="h-4 w-4" /> The Ladder · Est. 2026
                </div>
                <h1
                    className="text-6xl sm:text-7xl md:text-8xl leading-[0.9] text-white text-glow-red"
                    style={{ fontFamily: "'Alfa Slab One', 'Ultra', serif", fontWeight: 400 }}
                >
                    Climb the <span className="text-hellcat">ladder.</span>
                </h1>
                <h2
                    className="text-3xl sm:text-4xl md:text-5xl leading-[0.95] text-onyx-300 mt-2"
                    style={{ fontFamily: "'Alfa Slab One', 'Ultra', serif", fontWeight: 400 }}
                >
                    Written by <span className="text-hellcat">Remi.</span>
                </h2>
                <p className="mt-6 text-onyx-500 max-w-2xl text-lg leading-relaxed">
                    Every rung matters — R/T all the way up to Demon 170. Buyer's guides, spec breakdowns,
                    market pulse, and stories from the crew who's actually driven them.
                </p>
            </div>

            {/* Vertical Ladder Rail */}
            <div ref={railRef} className="relative max-w-7xl mx-auto px-5 pb-16" data-testid="the-ladder-rail">
                <div className="relative pl-14 md:pl-20">
                    {/* Track background */}
                    <div className="absolute left-6 md:left-9 top-4 bottom-4 w-[2px] bg-onyx-700" />
                    {/* Track fill (scroll-driven) */}
                    <motion.div
                        className="absolute left-6 md:left-9 top-4 w-[2px] bg-gradient-to-b from-hellcat via-hellcat to-hellcat-hover"
                        style={{ height: railFill, boxShadow: "0 0 12px rgba(225,6,0,0.7)" }}
                    />
                    {/* Rungs */}
                    <div className="space-y-4">
                        {LADDER.map((r, i) => (
                            <LadderRung key={r.rung} rung={r} idx={i} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function LadderRung({ rung, idx }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-100px" });
    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.35, delay: idx * 0.05, ease: [0.22, 1, 0.36, 1] }}
            className="relative group"
        >
            {/* Node dot on the rail */}
            <div className="absolute -left-[46px] md:-left-[62px] top-3 flex items-center justify-center">
                <div className="absolute h-3 w-3 rounded-full bg-hellcat glow-red group-hover:scale-150 transition-transform duration-200" />
                <div className="absolute h-6 w-6 rounded-full border border-hellcat/60 group-hover:scale-150 transition-transform duration-200" />
            </div>
            {/* Row */}
            <div className="brain-panel-elev p-4 md:p-5 flex items-center gap-4 md:gap-6 hover:border-hellcat transition-all duration-150 hover:scale-[1.005] cursor-default">
                <div className="brain-mono text-xs md:text-sm text-onyx-500 tabular-nums w-14 md:w-20">
                    RUNG<br />
                    <span className="text-hellcat text-lg md:text-2xl font-bold">{String(rung.rung).padStart(2, "0")}</span>
                </div>
                <div className="flex-1 min-w-0">
                    <div
                        className="text-2xl md:text-4xl text-white group-hover:text-hellcat transition-colors truncate"
                        style={{ fontFamily: "'Alfa Slab One', 'Ultra', serif", fontWeight: 400 }}
                    >
                        {rung.trim}
                    </div>
                    <div className="label-caps mt-1">Factory Rung</div>
                </div>
                <div className="text-right">
                    <div className="brain-mono text-2xl md:text-4xl text-hellcat tabular-nums font-bold text-glow-red">
                        {rung.hp}
                    </div>
                    <div className="label-caps">Horsepower</div>
                </div>
                <Zap className="hidden md:block h-6 w-6 text-onyx-500 group-hover:text-hellcat transition-colors" />
            </div>
        </motion.div>
    );
}

/* ================= POST CARD ================= */
function PostCard({ post, idx }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.04 }}
        >
            <Link
                to={`/the-ladder/${post.slug}`}
                data-testid={`post-card-${post.slug}`}
                className="block group brain-panel-elev p-6 hover:border-hellcat transition-all duration-150"
            >
                <div className="flex items-center gap-2 label-caps mb-3 text-hellcat">
                    <span>{(post.bucket || "").replace(/_/g, " ")}</span>
                    {post.is_real_testimonial && (
                        <span className="ml-auto brain-mono text-[10px] px-2 py-0.5 bg-emerald-500/15 text-emerald-300 normal-case tracking-normal border border-emerald-500/40">
                            REAL OWNER · VERIFIED
                        </span>
                    )}
                </div>
                <h3
                    className="text-2xl md:text-3xl leading-tight text-white group-hover:text-hellcat transition-colors"
                    style={{ fontFamily: "'DM Serif Display', serif" }}
                >
                    {post.title || "Untitled"}
                </h3>
                <p className="mt-3 text-sm text-onyx-500 line-clamp-3">{post.excerpt || ""}</p>
                <div className="mt-5 flex items-center justify-between label-caps text-onyx-600">
                    <span>By {post.author || "Remi"}</span>
                    <ArrowUpRight className="h-4 w-4 group-hover:text-hellcat group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </div>
            </Link>
        </motion.div>
    );
}

function EmptyState({ bucket }) {
    return (
        <div
            className="text-center py-20 brain-panel border-dashed"
            style={{ borderStyle: "dashed" }}
            data-testid="ladder-empty-state"
        >
            <div className="brain-mono text-hellcat text-xs mb-3 tracking-widest">// ENGINE WARM-UP</div>
            <h3
                className="text-3xl md:text-4xl text-white"
                style={{ fontFamily: "'Alfa Slab One', 'Ultra', serif", fontWeight: 400 }}
            >
                Warming up the blower
            </h3>
            <p className="mt-3 text-onyx-500 max-w-md mx-auto text-sm">
                {bucket === "all"
                    ? "Remi hasn't published yet. Once the engine is online, this feed fills up with 2 posts a day — no filler, no spam."
                    : "No posts in this bucket yet. Check back when Remi hits the next opportunity in this category."}
            </p>
        </div>
    );
}
